import FVector from '@data/geometry/FVector';
import { fromCurrentCoordinate, toCurrentCoordinate } from '@data/geodata/CoordinateSystemUtils';
import useNotificationStore from '@gui/store/notification';
import { useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { NotificationType } from '@visualizer/types';
import { useEffect, useMemo, useState } from 'react';
import { DrawModule } from '../../../rendering/DrawingModule/DrawingModule';
import { PolygonActionButtons } from '../DrawModule/PolygonActionButtons';
import { PolygonPointsList } from '../DrawModule/PolygonPointsList';

type PolygonDrawerProps = {
    isActive: boolean;
    initialPoints: FVector[];
    drawModule: DrawModule;
    disableSave?: boolean;
};

export function PolygonDrawer({ isActive, initialPoints = [], drawModule, disableSave = false }: PolygonDrawerProps) {
    // Zustand Variables:
    const showNotification = useNotificationStore((s) => s.showNotification);
    const coordinateSystem = useGlobalBucket((s) => s.settings.coordinateSystem);

    // React State:
    const round2 = (value: number) => Math.round(value * 100) / 100;
    const sanitize = (pts: FVector[]) =>
        pts
            .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z))
            .map((p) => new FVector(round2(p.x), round2(p.y), round2(p.z)));
    const [livePoints, setLivePoints] = useState<FVector[]>(sanitize(initialPoints));
    const [isDrawerRunning, setIsDrawerRunning] = useState<boolean>(!!drawModule?.isRunning?.());
    const displayedPoints = useMemo(
        () =>
            livePoints.map((point) => {
                const converted = toCurrentCoordinate(point, coordinateSystem);
                return new FVector(round2(converted.x), round2(converted.y), round2(converted.z));
            }),
        [coordinateSystem, livePoints]
    );

    // Subscribe Once:
    useEffect(() => {
        if (!drawModule) return;

        const onChange = (pts: FVector[]) => setLivePoints(sanitize(pts));
        const onStarted = () => setIsDrawerRunning(true);
        const onStopped = () => setIsDrawerRunning(false);
        drawModule.on('pointsChanged', onChange);
        drawModule.on('started', onStarted);
        drawModule.on('stopped', onStopped);

        return () => {
            drawModule.off('pointsChanged', onChange);
            drawModule.off('started', onStarted);
            drawModule.off('stopped', onStopped);
        };
    }, [drawModule]);

    // Activation handler:
    useEffect(() => {
        if (!drawModule) return;

        if (isActive) {
            drawModule.setPoints(sanitize(initialPoints));
            drawModule.tryActivate();

            showNotification(
                `💡 Hints:
                    • Single Click — Add Point
                    • Double Click First Point — Save Polygon
                    • Right Click on Point — Edit Mode
                    • Del/Backspace — Delete`,
                NotificationType.Info,
                true,
                true
            );
        } else {
            drawModule.stop();
        }
    }, [isActive]);

    // Actual HTML:
    return (
        <>
            {livePoints.length > 1 && isActive && (
                <PolygonPointsList
                    points={displayedPoints}
                    isEditingPolygon={isActive}
                    onChange={(pts) => drawModule.setPoints(sanitize(pts.map((point) => fromCurrentCoordinate(point, coordinateSystem))))}
                    onRemove={(i) => drawModule.deletePoint(i)}
                />
            )}

            <PolygonActionButtons
                positions={livePoints}
                draw2D={drawModule}
                onSave={() => drawModule.stop()}
                saveButtonId={'polygonDrawerSaveBtn'}
                disabled={disableSave}
                forceVisible={isDrawerRunning} // When editing hand drawn border, we need instantly the save button.
            />
        </>
    );
}

PolygonDrawer.displayName = 'PolygonDrawer';
